import React, { useEffect, useState } from "react";
import { ConflictInformation } from "../data/types";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { WarningIcon } from "../icons";
import { ConfirmationModal } from "./ConfirmationModal";
import { ConfirmationModalMeta } from "../data";
import { API_BASE_URL } from "../data";
import { XIcon } from "../icons";
import { useResourcePermissions } from "../hooks/useResourcePermissions";

type Props = {
    meta: any;
    conflicts: ConflictInformation[];
    isOpen: boolean;
    onCancel: () => void;
    title: string;
    message: string;
};

export const ConflictInformationModal = ({
    meta,
    conflicts,
    isOpen,
    onCancel,
    title,
    message,
}: Props) => {
    const [error, setError] = useState<string | null>(null);
    const [isDeallocateModalOpen, setIsDeallocateModalOpen] = useState(false);
    const [isDealloactionLoading, setIsDeallocationLoading] = useState(false);
    const [selectedConflict, setSelectedConflict] = useState<ConflictInformation | null>(null);
    const [allConflicts, setAllConflicts] = useState<ConflictInformation[]>([])
    const { canDeallocateConflict } = useResourcePermissions();


    console.log("Conflicts: ", conflicts);

    useEffect(() => {
        if (!isOpen) return;
        if (isOpen && conflicts && conflicts.length > 0)
            setAllConflicts(conflicts)
        else
            setError("No conflict information available.");
    }, [isOpen, conflicts]);

    const columns = meta.columns ?? [];

    const Tag = ({ children }: { children: React.ReactNode }) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-600 text-white text-xs font-medium">
            {children}
        </span>
    );

    const renderCell = (item: ConflictInformation, column: any) => {
        const value = item[column.key];

        switch (column.key) {
            case "type":
                return <Tag>{split_captitalize(value, '_')}</Tag>;
            case "start_time":
                return formatTime(value)
            case "end_time":
                return formatTime(value)
            case "action":
                return (
                    canDeallocateConflict() && <Button
                        type='button'
                        color="danger"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveAllocationClick(item)}
                    >
                        <XIcon className="w-4 h-4 me-1" />
                        Deallocate
                    </Button>
                );
            default:
                return value ?? "—";
        }
    };

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return 'N/A'
        const [hours, minutes] = timeStr.split(':')
        const hour = parseInt(hours)
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour % 12 || 12
        return `${displayHour}:${minutes} ${ampm}`
    }

    const split_captitalize = (str: string, split_char: string): string => {
        if (!str || !split_char) {
            return str;
        }

        return str.split(split_char)
            .map(word => {
                if (word.length === 0) {
                    return '';
                }
                return word[0].toUpperCase() + word.slice(1);
            })
            .join(' ');
    };

    const handleRemoveAllocationClick = (conflict: ConflictInformation) => {
        setSelectedConflict(conflict);
        setIsDeallocateModalOpen(true);
    }

    const handleRemoveAllocationClickClose = () => {
        setIsDeallocateModalOpen(false);
    }

    const handleRemoveAllocation = async () => {
        setIsDeallocationLoading(true);
        try {
            if (!selectedConflict) throw new Error("No Allocation Selected");

            const response = await fetch(`${API_BASE_URL}/allocation/shift/${selectedConflict.shift_id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    volunteer: [selectedConflict.volunteer_id],
                })
            });

            if (!response.ok) throw new Error("Failed to deallocate volunteer.");

            // Update only the shifts list, leads cannot be removed here
            setAllConflicts(prev => prev.filter(c => c.volunteer_id !== selectedConflict.volunteer_id && c.shift_id !== selectedConflict.shift_id));
            handleRemoveAllocationClickClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to deallocate volunteer.");
        }
        finally {
            setIsDeallocationLoading(false);
        }
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onCancel}
                title={title}
                showCloseButton={false}
                maxWidth="5xl"
                icon={
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-100 dark:bg-danger-900">
                        <WarningIcon className="h-6 w-6 text-danger-600 dark:text-danger-400" />
                    </div>
                }
                footer={
                    <div className="flex justify-end">
                        <Button onClick={onCancel} variant="outline" color="neutral">
                            Close
                        </Button>
                    </div>
                }
            >
                {error ? (
                    <div className="m-6 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                        {error}
                    </div>
                ) : (
                    <>
                        {/* Message */}
                        <div className="px-6 py-3">
                            <p className="text-md text-gray-500 dark:text-gray-400">
                                {message}
                            </p>
                        </div>

                        {/* Table */}
                        <div className="px-6 pb-6 overflow-x-auto">
                            <table className="w-full border-collapse table-auto text-sm dark:text-gray-300">
                                <thead className="bg-gray-100 dark:bg-gray-700">
                                    <tr>
                                        {columns.map((column) => (
                                            canDeallocateConflict() && <th
                                                key={column.key}
                                                className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap"
                                            >
                                                {column.header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {allConflicts?.map((conflict, index) => (
                                        <tr
                                            key={index}
                                            className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700"
                                        >
                                            {columns.map((column) => (
                                                <td
                                                    key={column.key}
                                                    className="px-3 py-2 border border-gray-600 align-top"
                                                >
                                                    {renderCell(conflict, column)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Modal>

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeallocateModalOpen}
                onConfirm={handleRemoveAllocation}
                onCancel={handleRemoveAllocationClickClose}
                title={"Deallocate Volunteer from the shift?"}
                message={"This action will remove the volunteer from this shift. This action cannot be undone."}
                isLoading={isDealloactionLoading}
                confirmButtonText="Deallocate"
                loadingButtonText="Deallocating..."
            />
        </>

    );
};
